/**
 * The public site's motion vocabulary, in the motion.dev idiom.
 *
 * Every component here follows three house rules:
 *
 *  1. Reduced motion is a first-class path, not an afterthought — each
 *     component collapses to its static final state under
 *     `prefers-reduced-motion`, never to a broken intermediate.
 *  2. Timing and easing come from `lib/motion.ts`, the same vocabulary the
 *     rest of the site already speaks. No component invents its own physics.
 *  3. Text stays text. Components that decorate copy (WordReveal) preserve the
 *     accessible string exactly — split words render with their spaces, so
 *     screen readers and tests read the same sentence the designer wrote.
 *
 * Deliberately NOT built, although they are in the motion.dev catalogue:
 * Confetti, Coverflow, CardStack, HoldToConfirm, PageCurtain. A protection
 * company's site trades on calm reliability; celebratory and game-like motion
 * works against the promise the copy is making.
 */
import {
  AnimatePresence,
  animate,
  motion,
  useInView,
  useMotionValue,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
} from 'framer-motion';
import {
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { Link } from 'react-router-dom';
import { publicMotion, publicTransition, publicVariants } from '../../lib/motion';
import './motion.css';

export const NARROW_MOTION_QUERY = '(max-width: 48rem)';

/** Keeps motion travel conservative at the public site's 430px and 390px acceptance widths. */
export function useNarrowMotionViewport() {
  const [narrow, setNarrow] = useState(() => (
    typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia(NARROW_MOTION_QUERY).matches
  ));

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const media = window.matchMedia(NARROW_MOTION_QUERY);
    const update = () => setNarrow(media.matches);
    update();
    media.addEventListener?.('change', update);
    return () => media.removeEventListener?.('change', update);
  }, []);

  return narrow;
}

/* Canonical public composition primitives. These are deliberately semantic:
   pages and sections remain landmarks while motion stays an enhancement. */
export function MotionPage({ children, className }: { children: ReactNode; className?: string }) {
  const reducedMotion = useReducedMotion();
  return <motion.div data-motion="page" className={className} initial={reducedMotion ? false : 'hidden'} animate="show" exit={reducedMotion ? undefined : 'exit'} variants={publicVariants.page} transition={publicTransition(!!reducedMotion)}>{children}</motion.div>;
}

export function MotionSection({ children, className, id, 'aria-label': ariaLabel }: { children: ReactNode; className?: string; id?: string; 'aria-label'?: string }) {
  const reducedMotion = useReducedMotion();
  return <motion.section id={id} aria-label={ariaLabel} data-motion="section" className={className} initial={reducedMotion ? false : 'hidden'} whileInView="show" viewport={publicMotion.viewport} variants={publicVariants.reveal} transition={publicTransition(!!reducedMotion, publicMotion.duration.scene)}>{children}</motion.section>;
}

export function MotionContainer({ children, className, stagger = false }: { children: ReactNode; className?: string; stagger?: boolean }) {
  const reducedMotion = useReducedMotion();
  return <motion.div data-motion="container" className={className} initial={stagger && !reducedMotion ? 'hidden' : false} whileInView={stagger ? 'show' : undefined} viewport={publicMotion.viewport} variants={stagger ? { hidden: {}, show: { transition: { staggerChildren: reducedMotion ? 0 : publicMotion.stagger.standard } } } : undefined}>{children}</motion.div>;
}

export function MotionReveal({ children, className, delay = 0 }: { children: ReactNode; className?: string; delay?: number }) {
  const reducedMotion = useReducedMotion();
  return <motion.div data-motion="reveal" className={className} initial={reducedMotion ? false : 'hidden'} whileInView="show" viewport={publicMotion.viewport} variants={publicVariants.reveal} transition={{ ...publicTransition(!!reducedMotion, publicMotion.duration.scene), delay: reducedMotion ? 0 : delay }}>{children}</motion.div>;
}

export function MotionStagger({ children, className }: { children: ReactNode; className?: string }) {
  const reducedMotion = useReducedMotion();
  return <motion.div data-motion="stagger" className={className} initial={reducedMotion ? false : 'hidden'} whileInView="show" viewport={publicMotion.viewport} variants={{ hidden: {}, show: { transition: { staggerChildren: reducedMotion ? 0 : publicMotion.stagger.standard } } }}>{children}</motion.div>;
}

export function MotionStaggerItem({ children, className }: { children: ReactNode; className?: string }) {
  const reducedMotion = useReducedMotion();
  return <motion.div data-motion="stagger-item" className={className} variants={reducedMotion ? undefined : publicVariants.reveal} transition={publicTransition(!!reducedMotion, publicMotion.duration.scene)}>{children}</motion.div>;
}

export function MotionArtwork({ children, className, support = false }: { children: ReactNode; className?: string; support?: boolean }) {
  const reducedMotion = useReducedMotion();
  const narrow = useNarrowMotionViewport();
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], [publicMotion.astronaut.parallax, -publicMotion.astronaut.parallax]);
  return <motion.div ref={ref} data-motion="artwork" className={`m-artwork${support ? ' m-artwork--support' : ''}${className ? ` ${className}` : ''}`} style={reducedMotion || narrow ? undefined : { y }}>{children}</motion.div>;
}

function hasAstronautIdle({ reduced, drift, coarse, narrow, priority }: { reduced: boolean; drift?: boolean; coarse: boolean; narrow?: boolean; priority?: boolean }) {
  return Boolean(drift && !coarse && !narrow && !priority && !reduced);
}

export function astronautMotion({ reduced, drift, coarse, narrow = false, priority = false }: { reduced: boolean; drift?: boolean; coarse: boolean; narrow?: boolean; priority?: boolean }) {
  if (reduced || priority) return { animate: publicVariants.astronautEntrance.show, transition: { duration: 0 } };

  const idle = hasAstronautIdle({ reduced, drift, coarse, narrow, priority });
  return {
    animate: idle
      ? { opacity: 1, y: [0, -publicMotion.astronaut.floatY, 0], rotate: [0, publicMotion.astronaut.rotate, 0], scale: 1 }
      : publicVariants.astronautEntrance.show,
    transition: idle
      ? {
        opacity: { duration: publicMotion.duration.scene, ease: publicMotion.ease.reveal },
        scale: { duration: publicMotion.duration.scene, ease: publicMotion.ease.reveal },
        rotate: { duration: publicMotion.duration.ambient, repeat: Infinity, ease: 'easeInOut' as const },
        y: { duration: publicMotion.duration.ambient, repeat: Infinity, ease: 'easeInOut' as const },
      }
      : publicTransition(false, publicMotion.duration.scene),
  };
}

export function MotionAstronaut({ src, width, height, priority, drift, className }: { src: string; width: number; height: number; priority?: boolean; drift?: boolean; className?: string }) {
  const reducedMotion = useReducedMotion();
  const narrow = useNarrowMotionViewport();
  const coarse = typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(pointer: coarse)').matches
    : true;
  const priorityAttrs = (priority ? { fetchpriority: 'high' } : {}) as Record<string, string>;
  const preset = astronautMotion({ reduced: Boolean(reducedMotion), drift, coarse, narrow, priority });
  const idle = hasAstronautIdle({ reduced: Boolean(reducedMotion), drift, coarse, narrow, priority });
  return <motion.img data-motion="astronaut" data-idle-motion={String(idle)} className={className} src={src} alt="" width={width} height={height} loading={priority ? 'eager' : 'lazy'} decoding={priority ? 'sync' : 'async'} {...priorityAttrs} initial={reducedMotion || priority ? false : publicVariants.astronautEntrance.hidden} animate={preset.animate} transition={preset.transition} />;
}

/* ------------------------------------------------------------------ */
/* BorderBeam — a travelling Enshield signal around protected surfaces */
/* ------------------------------------------------------------------ */

export function BorderBeamTrace({ duration = 5.5 }: { duration?: number }) {
  const reducedMotion = useReducedMotion();
  return (
    <motion.span
      aria-hidden="true"
      data-border-beam=""
      data-continuous-motion="false"
      className="m-borderbeam__trace"
      initial={reducedMotion ? false : { rotate: 0, opacity: 0.35 }}
      animate={{ rotate: reducedMotion ? 0 : 360, opacity: 0.55 }}
      transition={reducedMotion ? { duration: 0 } : { duration: Math.min(duration, 0.8), ease: publicMotion.ease.standard }}
    />
  );
}

export function BorderBeam({ children, className, motionName = 'border-beam' }: { children: ReactNode; className?: string; motionName?: string }) {
  const reducedMotion = useReducedMotion();
  return (
    <motion.div
      data-motion={motionName}
      className={`m-borderbeam${className ? ` ${className}` : ''}`}
      initial={false}
      whileHover={reducedMotion ? undefined : { y: -3, scale: 1.006 }}
      transition={publicMotion.spring.interface}
    >
      <BorderBeamTrace />
      <div className="m-borderbeam__content">{children}</div>
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/* WordReveal — staggered word rise for display headlines              */
/* ------------------------------------------------------------------ */

export function WordReveal({
  lines,
  as: Tag = 'span',
  delay = 0,
}: {
  /** Each entry renders as one line; words within a line stagger. */
  lines: string[];
  as?: 'span' | 'div';
  delay?: number;
}) {
  const reducedMotion = useReducedMotion();
  let wordIndex = 0;
  return (
    <Tag className="m-wordreveal">
      {lines.map((line, lineIdx) => (
        <span className="m-wordreveal__line" key={lineIdx}>
          {line.split(' ').map((word, i, arr) => {
            const idx = wordIndex++;
            return (
              <span className="m-wordreveal__mask" key={i}>
                <motion.span
                  className="m-wordreveal__word"
                  initial={reducedMotion ? false : { y: '110%', opacity: 0 }}
                  animate={{ y: '0%', opacity: 1 }}
                  transition={{
                    ...publicTransition(!!reducedMotion, publicMotion.duration.scene),
                    delay: reducedMotion ? 0 : delay + idx * 0.055,
                  }}
                >
                  {word}
                  {i < arr.length - 1 ? ' ' : ''}
                </motion.span>
              </span>
            );
          })}
        </span>
      ))}
    </Tag>
  );
}

/* ------------------------------------------------------------------ */
/* MagneticPull — the CTA leans toward the pointer                     */
/* ------------------------------------------------------------------ */

export function MagneticPull({
  children,
  strength = 0.28,
  className,
}: {
  children: ReactNode;
  strength?: number;
  className?: string;
}) {
  const reducedMotion = useReducedMotion();
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, publicMotion.spring.interface);
  const sy = useSpring(y, publicMotion.spring.interface);

  // Coarse pointers have no hover to be magnetic toward.
  // jsdom has no matchMedia; a test environment counts as a coarse pointer.
  const coarse =
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(pointer: coarse)').matches
      : true;
  const inert = !!reducedMotion || coarse;

  function onMove(event: ReactPointerEvent<HTMLDivElement>) {
    if (inert) return;
    const rect = event.currentTarget.getBoundingClientRect();
    x.set((event.clientX - rect.left - rect.width / 2) * strength);
    y.set((event.clientY - rect.top - rect.height / 2) * strength);
  }
  function onLeave() {
    x.set(0);
    y.set(0);
  }

  return (
    <motion.div
      className={className}
      style={inert ? undefined : { x: sx, y: sy, display: 'inline-block' }}
      onPointerMove={onMove}
      onPointerLeave={onLeave}
    >
      {children}
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/* ArrowLink — the arrow travels, the words hold still                 */
/* ------------------------------------------------------------------ */

export function ArrowLink({
  to,
  href,
  children,
  className,
}: {
  to?: string;
  href?: string;
  children: ReactNode;
  className?: string;
}) {
  const reducedMotion = useReducedMotion();
  const inner = (
    <motion.span className="m-arrowlink__inner" whileHover={reducedMotion ? undefined : 'hover'} initial="rest">
      <span>{children}</span>
      <motion.span
        aria-hidden="true"
        className="m-arrowlink__arrow"
        variants={reducedMotion ? undefined : { rest: { x: 0 }, hover: { x: 5 } }}
        transition={publicMotion.spring.interface}
      >
        →
      </motion.span>
    </motion.span>
  );
  const cls = `m-arrowlink${className ? ` ${className}` : ''}`;
  return to ? (
    <Link className={cls} to={to}>
      {inner}
    </Link>
  ) : (
    <a className={cls} href={href}>
      {inner}
    </a>
  );
}

/* ------------------------------------------------------------------ */
/* Accordion — one open at a time, height animated honestly            */
/* ------------------------------------------------------------------ */

export interface AccordionEntry {
  question: string;
  answer: ReactNode;
}

export function Accordion({
  items,
  defaultOpen = null,
  idBase = 'accordion',
}: {
  items: AccordionEntry[];
  /** Index opened on mount, or null for all closed. */
  defaultOpen?: number | null;
  idBase?: string;
}) {
  const reducedMotion = useReducedMotion();
  const [open, setOpen] = useState<number | null>(defaultOpen);

  return (
    <motion.div data-motion="accordion" className="m-accordion" layout>
      {items.map((item, index) => {
        const expanded = open === index;
        const buttonId = `${idBase}-h-${index}`;
        const panelId = `${idBase}-p-${index}`;
        return (
          <motion.div className="m-accordion__item" key={item.question} data-open={expanded} layout transition={publicMotion.spring.interface}>
            <h3 className="m-accordion__heading">
              <motion.button
                type="button"
                id={buttonId}
                aria-expanded={expanded}
                aria-controls={panelId}
                className="m-accordion__trigger"
                onClick={() => setOpen(expanded ? null : index)}
                whileHover={reducedMotion ? undefined : { x: 4 }}
                whileTap={reducedMotion ? undefined : { scale: 0.992 }}
                transition={publicMotion.spring.interface}
              >
                <span>{item.question}</span>
                <motion.i
                  aria-hidden="true"
                  className="m-accordion__chevron"
                  animate={{ rotate: expanded ? 45 : 0 }}
                  transition={publicTransition(!!reducedMotion)}
                />
              </motion.button>
            </h3>
            <AnimatePresence initial={false}>
              {expanded ? (
                <motion.div
                  key="panel"
                  id={panelId}
                  role="region"
                  data-motion="accordion-panel"
                  aria-labelledby={buttonId}
                  className="m-accordion__panel"
                  initial={reducedMotion ? { height: 'auto', opacity: 1 } : { height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={reducedMotion ? { height: 'auto', opacity: 1, transition: { duration: 0 } } : { height: 0, opacity: 0 }}
                  transition={publicTransition(!!reducedMotion, publicMotion.duration.interface)}
                >
                  <div className="m-accordion__body">{item.answer}</div>
                </motion.div>
              ) : null}
            </AnimatePresence>
          </motion.div>
        );
      })}
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/* CountUp — a number that arrives, once, when seen                    */
/* ------------------------------------------------------------------ */

export function CountUp({
  value,
  format = (n: number) => Math.round(n).toLocaleString('en-US'),
  duration = 1.2,
  className,
}: {
  value: number;
  format?: (n: number) => string;
  duration?: number;
  className?: string;
}) {
  const reducedMotion = useReducedMotion();
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, margin: '0px 0px -10% 0px' });
  const [text, setText] = useState(reducedMotion ? format(value) : format(0));

  useEffect(() => {
    if (!inView) return;
    if (reducedMotion) {
      setText(format(value));
      return;
    }
    const controls = animate(0, value, {
      duration,
      ease: publicMotion.ease.reveal,
      onUpdate: (latest) => setText(format(latest)),
    });
    return () => controls.stop();
  }, [duration, format, inView, reducedMotion, value]);

  return (
    <span ref={ref} className={className}>
      {text}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* CopyButton — copy morphs into confirmation, then back               */
/* ------------------------------------------------------------------ */

export function CopyButton({ value, label = 'Copy' }: { value: string; label?: string }) {
  const reducedMotion = useReducedMotion();
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      // Clipboard refused (permissions, insecure context): the value is still
      // on screen; do nothing rather than pretend.
    }
  }

  return (
    <button type="button" className="m-copybtn" onClick={copy} aria-live="polite">
      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={copied ? 'done' : 'idle'}
          initial={reducedMotion ? false : { opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={reducedMotion ? undefined : { opacity: 0, y: -4 }}
          transition={publicTransition(!!reducedMotion, publicMotion.duration.micro)}
        >
          {copied ? 'Copied' : label}
        </motion.span>
      </AnimatePresence>
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* ScrollProgress — a quiet reading indicator                          */
/* ------------------------------------------------------------------ */

export function ScrollProgress() {
  const reducedMotion = useReducedMotion();
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 140, damping: 28, mass: 0.6 });
  if (reducedMotion) return null;
  return <motion.div aria-hidden="true" className="m-scrollprogress" style={{ scaleX }} />;
}

/* ------------------------------------------------------------------ */
/* PageTransition — routes hand over, never jump-cut                   */
/* ------------------------------------------------------------------ */

export function PageTransition({ pathname, hash = '', children }: { pathname: string; hash?: string; children: ReactNode }) {
  return <AnimatePresence initial={false}><IncomingPage key={pathname} pathname={pathname} hash={hash}>{children}</IncomingPage></AnimatePresence>;
}

function IncomingPage({ pathname, hash, children }: { pathname: string; hash: string; children: ReactNode }) {
  const reducedMotion = useReducedMotion();
  const pageRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      let fragment = hash.slice(1);
      try { fragment = decodeURIComponent(fragment); } catch { /* A malformed escape can still be a literal element id. */ }
      const destination = fragment ? document.getElementById(fragment) : null;
      const target = destination ?? pageRef.current?.querySelector<HTMLElement>('h1') ?? pageRef.current;
      if (target) {
        target.tabIndex = -1;
        target.focus({ preventScroll: true });
      }
      if (destination) destination.scrollIntoView({ block: 'start', behavior: 'instant' });
      else if (import.meta.env.MODE !== 'test') window.scrollTo({ top: 0, behavior: 'instant' });
    });
    return () => cancelAnimationFrame(frame);
  }, [pathname, hash]);
  return (
      <motion.div
        ref={pageRef}
        data-motion="page-transition"
        data-motion-duration={publicMotion.duration.interface}
        className="m-page-transition"
        initial={reducedMotion ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={publicTransition(!!reducedMotion, publicMotion.duration.interface)}
      >
        <div data-motion="page">{children}</div>
      </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/* SceneReveal — semantic, reusable section-level entrance             */
/* ------------------------------------------------------------------ */

export function SceneReveal({
  as = 'div',
  children,
  className,
  delay = 0,
  id,
  role,
  'aria-label': ariaLabel,
}: {
  as?: 'div' | 'section' | 'article' | 'aside';
  children: ReactNode;
  className?: string;
  delay?: number;
  id?: string;
  role?: 'region' | 'group' | 'presentation';
  'aria-label'?: string;
}) {
  const reducedMotion = useReducedMotion();
  const narrow = useNarrowMotionViewport();
  const distance = narrow ? publicMotion.distance.micro : publicMotion.distance.scene;
  const Component = motion[as];
  return (
    <Component
      id={id}
      role={role}
      aria-label={ariaLabel}
      data-motion="scene-reveal"
      data-motion-distance={distance}
      className={className}
      initial={reducedMotion ? false : { opacity: 0, y: distance, filter: 'blur(8px)' }}
      whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
      viewport={{ once: true, amount: 0.12 }}
      transition={{ ...publicTransition(!!reducedMotion, publicMotion.duration.scene), delay: reducedMotion ? 0 : delay }}
    >
      {children}
    </Component>
  );
}

/* ------------------------------------------------------------------ */
/* RevealGroup / RevealItem — staggered arrival for grouped content    */
/* ------------------------------------------------------------------ */

export function RevealGroup({
  children,
  className,
  stagger = publicMotion.stagger.standard,
}: {
  children: ReactNode;
  className?: string;
  stagger?: number;
}) {
  const reducedMotion = useReducedMotion();
  return (
    <motion.div
      className={className}
      initial={reducedMotion ? false : 'hidden'}
      whileInView="show"
      viewport={{ once: true, margin: '0px 0px -10% 0px' }}
      variants={{ hidden: {}, show: { transition: { staggerChildren: reducedMotion ? 0 : stagger } } }}
    >
      {children}
    </motion.div>
  );
}

export function RevealItem({ children, className }: { children: ReactNode; className?: string }) {
  const reducedMotion = useReducedMotion();
  return (
    <motion.div
      className={className}
      variants={{
        hidden: reducedMotion ? {} : { opacity: 0, y: publicMotion.distance.interface },
        show: { opacity: 1, y: 0, transition: publicTransition(!!reducedMotion, publicMotion.duration.scene) },
      }}
    >
      {children}
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/* useParallax — a restrained vertical drift for decorative media      */
/* ------------------------------------------------------------------ */

export function ParallaxDrift({
  children,
  distance = 24,
  className,
}: {
  children: ReactNode;
  distance?: number;
  className?: string;
}) {
  const reducedMotion = useReducedMotion();
  const narrow = useNarrowMotionViewport();
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], [distance, -distance]);
  return (
    <motion.div ref={ref} className={className} style={reducedMotion || narrow ? undefined : { y }}>
      {children}
    </motion.div>
  );
}
